import styled from '@emotion/styled';
import { BaseButton, Variables } from '../base';

export const Loader = styled.div`
    display: flex;
    flex-direction: column;
    flex: 0 1 40%;
    justify-content: center;

    & > svg {
        background-color: transparent;
    }
`;

export const HeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
`;

export const HeaderTitleBlock = styled.div`
    min-width: 0;
`;

export const PageTitle = styled.h1`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: ${Variables.Colors.n100};
`;

export const PageSubtitle = styled.div`
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.4;
    color: ${Variables.Colors.n70};
`;

export const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

export const StatementButtonGroup = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

export const HeaderOutlinedButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    min-width: auto;
    padding: 0 12px;
    background: ${Variables.Swatches.White};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.Colors.n100};

    &:hover:not(:disabled) {
        background: ${Variables.Colors.n15_24};
        color: ${Variables.Colors.n100};
    }

    &:disabled {
        opacity: 0.6;
    }
`;

export const HeaderGhostButton = styled(HeaderOutlinedButton)`
    background: transparent;
    color: ${Variables.Colors.n70};

    &:hover:not(:disabled) {
        background: ${Variables.Colors.n15_24};
        color: ${Variables.Colors.n100};
    }
`;

export const HeaderPrimaryButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    min-width: auto;
    padding: 0 14px;
    background: ${Variables.Colors.b300};
    border: 1px solid ${Variables.Colors.b300};
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.Swatches.White};

    &:hover:not(:disabled) {
        background: ${Variables.Colors.b300_l62};
        border-color: ${Variables.Colors.b300_l62};
        color: ${Variables.Swatches.White};
    }

    &:disabled {
        opacity: 0.6;
    }
`;

export const ActionButton = styled(BaseButton)`
    min-width: 120px;
`;


export const BalanceCard = styled.div`
    margin-bottom: 24px;
    background: ${Variables.Swatches.White};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 12px;
    box-shadow: 0 1px 2px ${Variables.Colors.nx4};
    /* PageLayout is a column flex container. Flex items with overflow != visible
       resolve min-height:auto to 0 and the grid collapses — DOM stays, paint doesn't. */
    overflow: hidden;
    flex-shrink: 0;
    min-height: min-content;
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
    width: 100%;
    min-width: 0;

    @media (max-width: 1100px) {
        grid-template-columns: 1fr;
    }
`;

export const BalanceHeroMain = styled.div<{ paidUp?: boolean }>`
    padding: 24px 28px;
    border-right: 1px solid ${Variables.ThemeLight.Border1};
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: ${props => (props.paidUp ? Variables.Swatches.Green1 : Variables.Swatches.White)};
    min-height: 0;

    @media (max-width: 1100px) {
        border-right: none;
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    }
`;

export const BalanceHeroLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    text-transform: uppercase;
    letter-spacing: 0.06em;
`;

export const BalanceAmount = styled.div<{ paidUp?: boolean }>`
    color: ${props => (props.paidUp ? Variables.Colors.n100 : Variables.Swatches.Red6)};
    font-size: 40px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
`;

export const BalanceTag = styled.div<{ paidUp?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background: ${props => (props.paidUp ? Variables.Swatches.Green2 : Variables.Swatches.Red2)};
    color: ${props => (props.paidUp ? Variables.Swatches.Green8 : Variables.Swatches.Red8)};
`;

export const BalanceTagDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
`;

export const BalanceNote = styled.div`
    font-size: 12px;
    color: ${Variables.Colors.n70};
    margin-top: 8px;
`;

export const BalanceBreakdown = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-width: 0;
    min-height: 0;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

export const BreakdownItem = styled.div`
    padding: 20px 18px;
    border-right: 1px solid ${Variables.ThemeLight.Border1};
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    &:last-child {
        border-right: none;
    }

    @media (max-width: 1100px) {
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};

        &:nth-child(even) {
            border-right: none;
        }

        &:nth-last-child(-n + 2) {
            border-bottom: none;
        }
    }
`;

export const BreakdownItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

export const BreakdownItemLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

export const BreakdownItemValue = styled.div`
    font-size: 21px;
    font-weight: 600;
    color: ${Variables.Colors.n100};
    font-variant-numeric: tabular-nums;
    line-height: 1;
`;

export const BreakdownItemSub = styled.div`
    font-size: 12px;
    color: ${Variables.Colors.n70};
`;

export const PayButton = styled(BaseButton)`
    min-width: 120px;
`;

export const CardsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
    position: relative;
    z-index: 0;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const HalfWidthCard = styled.div`
    min-width: 0;
    background: ${Variables.Swatches.White};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    box-shadow: 0 1px 2px ${Variables.Colors.nx4};
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
`;

export const SectionLabel = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${Variables.Colors.b300};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 14px;
`;

export const CardContent = styled.div`
    display: grid;
    grid-template-columns: 104px 1fr;
    row-gap: 9px;
    column-gap: 8px;
    font-size: 14px;
    line-height: 1.5;
`;

export const PatientName = styled.div`
    color: ${Variables.Colors.n100};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ProviderName = styled.div`
    color: ${Variables.Colors.n100};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 14px;
`;

export const SexBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    background: #e6dde6;
    color: #6f4f6f;
    border: 1px solid ${Variables.ThemeLight.Border1};
    font-size: 10px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const InfoLabel = styled.div`
    color: ${Variables.Colors.n70};
`;

export const InfoValue = styled.div`
    color: ${Variables.Colors.n100};
    font-variant-numeric: tabular-nums;
`;

export const InfoValueMuted = styled.div`
    color: ${Variables.Colors.n70};
`;

export const EmailLink = styled.a`
    color: ${Variables.Colors.b300};
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

export const TableCardWrapper = styled.div`
    margin-bottom: 24px;

    .summary-row {
        font-weight: 600;
        border-top: 1px solid ${Variables.ThemeLight.Border1};
        background-color: ${Variables.Colors.n15_24};
        
        &:hover {
            background-color: ${Variables.Colors.n15_24} !important;
        }
    }

    .pending-row {
        background-color: ${Variables.ThemeLight.Background1};

        &:hover {
            background-color: ${Variables.Swatches.Gray3} !important;
        }
    }
`;

export const TableCardTitle = styled.div`
    color: ${Variables.Colors.p500};
    font-size: 16px;
    font-weight: 600;
    padding: 20px 0;
`;

export const TableCardTitleCompact = styled(TableCardTitle)`
    padding: 0;
`;

export const TableHeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 20px 0;
`;

export const FilterRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
`;

export const FilterLabel = styled.span`
    color: ${Variables.Colors.n70};
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    margin-right: 4px;
`;

export const FilterSeparator = styled.span`
    color: ${Variables.Colors.n70};
    font-size: 16px;
    padding: 0 4px;
`;

export const TabPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const TabActionsRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const CustomAIButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    min-width: unset;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    border-radius: 8px !important;
    border: 1px solid var(--primary-strong, ${Variables.ThemeLight.PrimaryConstant6});
    background-color: transparent;
    color: var(--primary-strong, ${Variables.ThemeLight.PrimaryConstant6});
    filter: none;
    cursor: pointer;
    transition:
        background 120ms ease-out,
        border-color 120ms ease-out;

    & > img {
        display: inline-block;
        height: 16px;
        width: auto;
    }

    &:hover:not(:disabled) {
        background-color: var(--primary-x-mild, ${Variables.ThemeLight.PrimaryMild2});
    }

    &:active:not(:disabled) {
        background-color: var(--primary-mild, ${Variables.ThemeLight.PrimaryMild1});
    }

    &:disabled {
        color: var(--text-x-mild, ${Variables.ThemeLight.TextXMild});
        border-color: var(--border, ${Variables.ThemeLight.Border1});
        cursor: not-allowed;
        filter: none;
        background-color: transparent;
    }
`;

export const DiagnosisCodesContainer = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
`;
export const AskButtonWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 8px;
    width: 105px;
    height: 80px;
    overflow: visible;
    font-size: 14px;

    & > * {
        position: relative;
        z-index: 1;
    }

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: inherit;
        opacity: 0;
        filter: blur(16px);
        background: radial-gradient(
            circle at center,
            ${Variables.ThemeLight.SuccessInverted9},
            ${Variables.ThemeLight.PrimaryInverted9},
            ${Variables.ThemeLight.WarningInverted9},
            ${Variables.ThemeLight.AttentionInverted9}
        );
        pointer-events: none;
        transition: opacity 0.2s ease;
        z-index: 0;
    }

    &:hover::before {
        opacity: 0.5;
    }

    &:active::before {
        opacity: 0.6;
    }
`;

export const PaymentActions = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
`;

export const PaymentActionButton = styled(BaseButton)`
    min-width: 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 6px;
    color: ${Variables.Colors.n70};

    &:hover {
        color: ${Variables.Colors.n100};
        background: ${Variables.Colors.n15_24};
    }
`;
